import axios from 'axios';
import { streamRealtimeTurn } from './api';

// GA Realtime API WebRTC endpoint. The model is set server-side when the
// ephemeral client secret is minted, so it is not repeated here.
const REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

const HALLUCINATION_PATTERNS = [
  /^(bye\.?\s*)+$/i,
  /^(thank you\.?\s*)+$/i,
  /^(you\.?\s*)+$/i,
  /^(yes\.?\s*)+$/i,
  /^(no\.?\s*)+$/i,
  /^\.+$/,
];

function isHallucination(text) {
  const t = text.trim();
  if (t.length < 3) return true;
  return HALLUCINATION_PATTERNS.some(re => re.test(t));
}

function previewText(text, length = 120) {
  if (!text) return '';
  return text.length > length ? `${text.slice(0, length)}…` : text;
}

// Streamed turn metadata -> the analytics shape the voice callbacks already use.
function toVoiceAnalytics(meta) {
  return {
    ...meta,
    response: meta.smoker_response?.text || '',
    counsellor_message: meta.counselor_message?.text || '',
    system_messages: {
      counsellor: meta.counselor_message?.system_messages || [],
      client: meta.smoker_response?.system_messages || [],
    },
  };
}

export class RealtimeService {
  // role is the human speaker's role: 'counsellor' (full session, AI plays the
  // smoker client) or 'client' (be-the-client, AI plays the counsellor).
  constructor({
    onTranscript,
    onClientResponse,
    onAnalytics,
    onClassification,
    onGuidance,
    onGuidanceError,
    onStatusChange,
    onError,
    role = 'counsellor',
  }) {
    this.onTranscript = onTranscript;
    this.onClientResponse = onClientResponse;
    this.onAnalytics = onAnalytics;
    this.onClassification = onClassification;
    this.onGuidance = onGuidance;
    this.onGuidanceError = onGuidanceError;
    this.onStatusChange = onStatusChange;
    this.onError = onError;
    this.role = role;

    this.pc = null;
    this.dc = null;
    this.audioEl = null;
    this.stream = null;

    this._phase = 'idle';       // 'idle' | 'tool_call' | 'speaking'
    this._pendingData = null;   // MIBot response data once Flask returns
    this._toolResponseDone = false; // true if response.done fired while phase === 'tool_call'
    // Full Session turns stream (classification, reply, guidance, metadata).
    this._turnSettled = null;       // resolves once the previous streamed turn is saved
    this._streamReplyShown = true;  // metadata waits until the reply is on screen
    this._deferredAnalytics = null;
  }

  _deliverStreamAnalytics(analytics) {
    if (this._streamReplyShown) {
      this.onAnalytics(analytics);
    } else {
      this._deferredAnalytics = analytics;
    }
  }

  _markStreamReplyShown() {
    this._streamReplyShown = true;
    const deferred = this._deferredAnalytics;
    this._deferredAnalytics = null;
    if (deferred) this.onAnalytics(deferred);
  }

  // Resolves with { response } as soon as the reply text is ready; guidance and
  // metadata keep arriving afterwards through the callbacks.
  _streamCounsellorTurn(transcript) {
    this._streamReplyShown = false;
    this._deferredAnalytics = null;
    return new Promise((resolve, reject) => {
      let replied = false;
      const stream = streamRealtimeTurn(transcript, {
        onClassification: (data) => this.onClassification?.(data.automiti_labels || []),
        onGuidance: (feedback) => this.onGuidance?.(feedback),
        onGuidanceError: (message) => this.onGuidanceError?.(message),
        onClientResponse: (text) => {
          replied = true;
          resolve({ response: text, streamed: true });
        },
        onMetadata: (meta) => this._deliverStreamAnalytics(toVoiceAnalytics(meta)),
      });
      this._turnSettled = stream.promise.then(
        () => {
          // Session ended without a client reply: nothing to speak.
          if (!replied) resolve({ response: '', streamed: true });
        },
        (err) => {
          if (replied) {
            this.onError(err);
          } else {
            reject(err);
          }
        },
      );
    });
  }

  _muteMic() {
    if (this.stream) this.stream.getAudioTracks().forEach(t => { t.enabled = false; });
  }

  _unmuteMic() {
    if (this.stream) this.stream.getAudioTracks().forEach(t => { t.enabled = true; });
  }

  // Called when both conditions are met: Flask returned AND response.done fired
  _triggerSpeaking() {
    const data = this._pendingData;
    this._pendingData = null;
    this._toolResponseDone = false;
    this._phase = 'speaking';
    this.onStatusChange('speaking');
    console.log('[Realtime] trigger speaking', {
      hasData: !!data,
      responsePreview: previewText(data?.response || ''),
    });

    // Disable server-side VAD so OpenAI stops listening while the client speaks
    this._send({
      type: 'session.update',
      session: { type: 'realtime', audio: { input: { turn_detection: null } } },
    });

    if (!data) {
      // Nothing to voice — skip the speaking response or we'd hang in
      // the speaking phase waiting for a response.done that never comes.
      this._resumeListening();
      return;
    }

    if (!data.response) {
      // Turn ended with no reply text (e.g. observers ended the session right
      // after the user's message) — deliver analytics but skip TTS.
      if (data.streamed) {
        this._markStreamReplyShown();
      } else {
        this.onAnalytics(data);
      }
      this._resumeListening();
      return;
    }

    console.log('[Realtime] UI/client response append', previewText(data.response));
    this.onClientResponse(data.response);
    if (data.streamed) {
      this._markStreamReplyShown();
    } else {
      this.onAnalytics(data);
    }

    // Audio response constrained to reading the MIBot reply word for word;
    // tool_choice: 'none' so the model can't call the tool again.
    this._send({
      type: 'response.create',
      response: {
        output_modalities: ['audio'],
        tool_choice: 'none',
        instructions:
          'Say the following reply exactly, word for word, with natural conversational delivery. ' +
          'Do not add, omit, translate, or rephrase anything:\n\n' +
          data.response,
      },
    });
  }

  _resumeListening() {
    // Re-enable VAD and unmute mic with a short grace period after audio finishes
    setTimeout(() => {
      this._send({
        type: 'session.update',
        session: {
          type: 'realtime',
          audio: {
            input: {
              turn_detection: {
                type: 'server_vad',
                threshold: 0.5,
                prefix_padding_ms: 300,
                silence_duration_ms: 600,
              },
            },
          },
        },
      });
      this._phase = 'idle';
      this._unmuteMic();
      this.onStatusChange('listening');
    }, 300);
  }

  async connect() {
    try {
      this.onStatusChange('connecting');

      const sessionResp = await axios.post('/api/realtime/session');
      const { client_secret } = sessionResp.data;
      const ephemeralKey = client_secret.value;

      this.audioEl = document.createElement('audio');
      this.audioEl.autoplay = true;
      this.audioEl.volume = 1.0;
      document.body.appendChild(this.audioEl);

      this.pc = new RTCPeerConnection();
      this.pc.ontrack = (e) => {
        if (e.streams && e.streams[0]) {
          this.audioEl.srcObject = e.streams[0];
          this.audioEl.play().catch(err => console.warn('Audio play error:', err));
        }
      };

      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      this.stream.getTracks().forEach(track => this.pc.addTrack(track, this.stream));

      this.dc = this.pc.createDataChannel('oai-events');
      this.dc.onopen = () => this._configureSession();
      this.dc.onmessage = (e) => this._handleEvent(JSON.parse(e.data));

      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);

      const sdpResp = await fetch(REALTIME_CALLS_URL, {
        method: 'POST',
        body: offer.sdp,
        headers: { Authorization: `Bearer ${ephemeralKey}`, 'Content-Type': 'application/sdp' },
      });
      if (!sdpResp.ok) {
        const errBody = await sdpResp.text().catch(() => '');
        console.error('[Realtime] SDP negotiation failed', sdpResp.status, errBody);
        throw new Error(`SDP negotiation failed (${sdpResp.status}): ${errBody || 'no error body'}`);
      }
      await this.pc.setRemoteDescription({ type: 'answer', sdp: await sdpResp.text() });

      this.onStatusChange('listening');
    } catch (err) {
      this.onError(err);
      this.disconnect();
    }
  }

  _configureSession() {
    const speakerLabel = this.role === 'client' ? 'client' : 'counsellor';
    const responderLabel = this.role === 'client' ? 'counsellor' : 'client';
    const toolName = this.role === 'client' ? 'get_counsellor_response' : 'get_client_response';
    // GA session shape: transcription and turn detection live under
    // session.audio.input; session.type is required.
    this._send({
      type: 'session.update',
      session: {
        type: 'realtime',
        // Text-only by default: the VAD-triggered response can only emit the
        // tool call, never spoken audio of its own. Audio is re-enabled
        // per-response in _triggerSpeaking to voice the MIBot reply verbatim.
        output_modalities: ['text'],
        instructions:
          'You are a voice interface for a Motivational Interviewing training system. ' +
          `When the user finishes speaking, call the ${toolName} tool with their transcript. ` +
          'You are never the one talking to the user: never answer, greet, or comment yourself. ' +
          'Your only job is to relay transcripts to the tool and speak its result when told to.',
        tools: [{
          type: 'function',
          name: toolName,
          description: `Get the MI ${responderLabel} response to the ${speakerLabel} utterance`,
          parameters: {
            type: 'object',
            properties: {
              transcript: { type: 'string', description: `Verbatim transcript of what the ${speakerLabel} said` },
            },
            required: ['transcript'],
          },
        }],
        tool_choice: 'required',
        audio: {
          input: {
            transcription: { model: 'whisper-1' },
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 600,
            },
          },
        },
      },
    });
  }

  async _handleEvent(event) {
    switch (event.type) {

      case 'input_audio_buffer.speech_started':
        if (this._phase === 'idle') this.onStatusChange('listening');
        break;

      case 'conversation.item.input_audio_transcription.completed': {
        const text = (event.transcript || '').trim();
        console.log('[Realtime] transcript completed', previewText(text));
        if (text && !isHallucination(text)) this.onTranscript(text);
        break;
      }

      case 'response.function_call_arguments.done': {
        if (this._phase !== 'idle') break;
        this._phase = 'tool_call';
        this._muteMic();
        this.onStatusChange('processing');

        const { call_id } = event;
        let transcript = '';
        try { transcript = JSON.parse(event.arguments).transcript; } catch {}
        console.log('[Realtime] tool call start', { call_id, transcriptPreview: previewText(transcript) });

        // A streamed turn is saved when its metadata lands; never start the
        // next one on top of it.
        if (this._turnSettled) await this._turnSettled;

        try {
          const data = this.role === 'client'
            ? (await axios.post('/api/realtime/respond', { transcript })).data
            : await this._streamCounsellorTurn(transcript);
          console.log('[Realtime] backend response', {
            call_id,
            responsePreview: previewText(data?.response || ''),
          });

          // Send the tool result to the Realtime session
          this._send({
            type: 'conversation.item.create',
            item: { type: 'function_call_output', call_id, output: data.response },
          });
          console.log('[Realtime] sent tool output', { call_id, outputPreview: previewText(data?.response || '') });

          this._pendingData = data;

          if (this._toolResponseDone) {
            // response.done already fired while we were waiting — trigger speaking now
            this._triggerSpeaking();
          }
          // else: wait for response.done to call _triggerSpeaking

        } catch (err) {
          this._phase = 'idle';
          this._toolResponseDone = false;
          this._unmuteMic();
          this.onStatusChange('listening');
          if ((err?.response?.status ?? err?.status) === 409) {
            console.warn('Realtime: discarded overlapping tool call');
          } else {
            this.onError(err);
          }
        }
        break;
      }

      case 'response.done': {
        console.log('[Realtime] response.done', {
          phase: this._phase,
          hasPendingData: !!this._pendingData,
          toolResponseDone: this._toolResponseDone,
        });
        if (this._phase === 'tool_call') {
          // Flask call still in progress — set flag, _triggerSpeaking will fire when it completes
          this._toolResponseDone = true;

        } else if (this._phase === 'speaking') {
          // TTS finished — back to listening (grace period prevents self-talk)
          this._resumeListening();

        } else if (this._pendingData) {
          // Edge case: Flask returned before response.done but phase got desynced
          this._triggerSpeaking();
        }
        break;
      }

      case 'error': {
        const msg = event.error?.message || '';
        if (/cancellation failed|no active response/i.test(msg)) {
          console.warn('Realtime non-fatal:', msg);
          break;
        }
        console.error('Realtime API error:', msg);
        this.onError(new Error(msg || 'Realtime API error'));
        break;
      }

      default:
        break;
    }
  }

  _send(obj) {
    if (this.dc && this.dc.readyState === 'open') {
      this.dc.send(JSON.stringify(obj));
    }
  }

  disconnect() {
    if (this.dc) { this.dc.close(); this.dc = null; }
    if (this.pc) { this.pc.close(); this.pc = null; }
    if (this.stream) { this.stream.getTracks().forEach(t => t.stop()); this.stream = null; }
    if (this.audioEl) { this.audioEl.srcObject = null; this.audioEl.remove(); this.audioEl = null; }
    this._phase = 'idle';
    this._pendingData = null;
    this._toolResponseDone = false;
    // A streamed turn still in flight keeps running so it gets saved; let its
    // analytics through even though its reply will never be spoken.
    this._markStreamReplyShown();
    this.onStatusChange('idle');
  }
}
